import {Inject, Injectable, PLATFORM_ID} from '@angular/core';
import {EventService, Product, ProductSearchService} from '@spartacus/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NavigationEnd, Router} from '@angular/router';
import {isPlatformBrowser} from '@angular/common';
import {EddlSearchInitiatedEvent} from './events/eddl-search-initiated-event';
import {EddlSearchNoResultsEvent} from './events/eddl-search-no-results-event';
import {filter, first} from 'rxjs/operators';
import {EddlSearchClickType} from './models/eddl-adobe-tracking-data';
import {EddlSearchResultsClickEvent} from './events/eddl-search-results-click-event';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class DcpEddlSearchTrackingService {

  paddingBase: string = 'https://example.com';
  solutionSuggestionType: EddlSearchClickType = 'solution suggestions';

  public sortFilterOrPaginationHitEvent: boolean = false;
  public searchClickType: EddlSearchClickType = 'recently searched';
  private solutionClicked: Product; // via solution suggestions
  private productClicked: Product; // via search results page
  private clickRank: number = 0;
  private navigatedSinceLastTrack: boolean = true;
  private lastSolutionClicked: string;
  private lastSearchTerm: string;
  private searchType: EddlSearchClickType;
  private pageNumber: number;

  constructor(
    @Inject(PLATFORM_ID) public platformId: object,
    private router: Router,
    private productSearchService: ProductSearchService,
    private eventService: EventService,
  ) {
    this.setupSearchTracking();
  }

  /**
   * Sets up the search tacking based on navigation events and changes in the search results.
   */
  private setupSearchTracking(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.router.events.pipe(
        filter(e => e instanceof NavigationEnd),
        untilDestroyed(this),
      ).subscribe(() => {
        this.navigatedSinceLastTrack = true;
      });
      this.productSearchService.getResults().pipe(untilDestroyed(this)).subscribe(results => {
        if (
          this.navigatedSinceLastTrack
          && window.location.href.match(/\/search([/?]|$)/)
          && results?.freeTextSearch
        ) {
          this.trackKeywordSearch(results.freeTextSearch, !results.products.length);
        }
      });
    }
  }

  /**
   * Track search by keyword and solutions clicked via dropdown.
   * Send data to the data layer
   *
   * @param term keyword(s) used
   * @param searchNoResults result list is empty
   */
  private trackKeywordSearch(
    term: string,
    searchNoResults: boolean
  ): void {
    if (this.shouldTriggerSearchEvents(term)) {
      this.setValues(term);
      this.eventService.dispatch(new EddlSearchInitiatedEvent());

      if (searchNoResults) {
        this.eventService.dispatch(new EddlSearchNoResultsEvent());
      }

      if (this.searchClickType === this.solutionSuggestionType) {
        this.throwSearchResultsClickEvent();
      }
      this.navigatedSinceLastTrack = false;
      this.sortFilterOrPaginationHitEvent = false;
    }
  }

  private setValues(term: string): void {
    this.lastSearchTerm = term;
    this.lastSolutionClicked = this.solutionClicked?.code;
    this.searchType = this.searchClickType;
  }

  /**
   * Helper function for setting all the values at once on solution suggestion click
   *
   * @param term keyword(s) used
   * @param product solution a user clicked on
   * @param clickRank index of solution clicked
   */
  public triggerSearchViaSolutionSuggestion(term: string, product: Product, clickRank: number): void {
    this.solutionClicked = product;
    this.clickRank = clickRank;
    this.pageNumber = 0;
    this.searchClickType = this.solutionSuggestionType;
    this.trackKeywordSearch(term, false);
  }

  /**
   * Track solutions clicked via search results page.
   * Send data to the data layer
   *
   * @param url url of the page with solution clicked
   * @param product solution a user clicked on
   * @param clickRank index of solution clicked
   */
  public trackSearchResultsClick(
    url: string,
    clickRank: number,
    product: Product
  ): void {
    this.clickRank = clickRank;
    this.productClicked = product;
    // exclude empty search, incl. sort, filtering, pagination on empty search
    if (!url.match(/\/search([?]|\/?$)/)) {
      this.setPageNumber(url);
      // to reset solution suggestion value once double click on one and the same solution
      this.searchType = this.searchClickType === this.solutionSuggestionType
        ? 'keyword search' : this.searchClickType;
      this.throwSearchResultsClickEvent();
    }
  }

  /**
   * Make sure navigation has ended and a user is on a product details page.
   */
  private throwSearchResultsClickEvent(): void {
    const regex: RegExp = /\/product([/?]|$)/;
    if (!window.location.href.match(regex)) {
      this.router.events.pipe(
        filter(e => e instanceof NavigationEnd),
        untilDestroyed(this),
        first()
      ).subscribe(() => {
        if (window.location.href.match(regex)) {
          this.eventService.dispatch(new EddlSearchResultsClickEvent());
        }
      });
    } else {
      this.eventService.dispatch(new EddlSearchResultsClickEvent());
    }
    // to reset any value once back to search results page if any
    this.searchClickType = 'keyword search';
  }

  /**
   * Helper function to make sure all conditions for triggering search events are met.
   *
   * @param term keyword(s) used
   */
  private shouldTriggerSearchEvents(term: string): boolean {
    let searchTypeSpecificChecks: boolean = false;

    if (this.searchClickType === this.solutionSuggestionType) {
      // do not trigger events on one and the same product click twice in a row
      // unless there are different search types in between
      searchTypeSpecificChecks = this.lastSolutionClicked !== this.solutionClicked?.code
        || this.searchClickType !== this.searchType;
    } else {
      searchTypeSpecificChecks = this.navigatedSinceLastTrack || this.lastSearchTerm !== term;
    }

    return isPlatformBrowser(this.platformId) && term && searchTypeSpecificChecks
      // do not trigger on sort, filter, and page change
      && !this.sortFilterOrPaginationHitEvent;
  }

  /**
   * Gets page number from the URL
   * Specifically for search results page click cases
   * The first page does not have 'currentPage' parameter on initial load
   */
  private setPageNumber(url: string): void {
    const currentPage = Number(new URL(url, this.paddingBase).searchParams.get('currentPage'));
    this.pageNumber = currentPage ? currentPage + 1 : 1;
  }

  public getSearchTerm(): string {
    return this.lastSearchTerm?.replace(/[^\x00-\x7F]/g, '').toLowerCase().slice(0, 100); // NOSONAR
  }

  public getProductClicked(): Product {
    return this.searchType === this.solutionSuggestionType ? this.solutionClicked : this.productClicked;
  }

  public getSearchType(): EddlSearchClickType {
    return this.searchType;
  }

  public getPageNumber(): number {
    return this.pageNumber;
  }

  public getClickRank(): number {
    return this.clickRank + 1;
  }
}


